import React, { useEffect, useState, useRef, useMemo } from "react";
import { ToastProvider } from "./components/UI";
import {
  BrowserRouter as Router,
  Route,
  Redirect,
  Switch,
  Link,
  useLocation,
  RouteProps,
} from "react-router-dom";
import { Provider, connect } from "react-redux";
import {
  LayoutDashboard,
  ClipboardList,
  Building2,
  Store,
  Users,
  UserPlus,
  PieChart,
  LogOut,
  User,
  Menu as MenuIcon,
  ChevronLeft,
  ChevronRight,
  Bell,
  ShieldCheck,
  X,
  ShieldOff,
} from "lucide-react";
import store from "./store";
import { Login } from "./components";
import { getCurrentuser, logoutUser } from "./store/actions";
import RequestList from "./components/Requests/RequestList";
import SiteList from "./components/Sites/SiteList";
import AddSite from "./components/Sites/AddSite";
import AddRequest from "./components/Requests/AddRequest";
import EditRequest from "./components/Requests/EditRequest";
import EditSite from "./components/Sites/EditSite";
import AddDoor from "./components/Doors/AddDoor";
import DoorList from "./components/Doors/DoorList";
import EditDoor from "./components/Doors/EditDoor";
import AddApprover from "./components/Approver/AddApprover";
import ApproverList from "./components/Approver/ApproverList";
import Dashboard from "./components/Dashboard/Dashboard";
import EditApprover from "./components/Approver/EditApprover";
import DelegationListByAdmin from "./components/DelegationAdmin/DelegationListByAdmin";
import UnifiedReport from "./components/Reports/UnifiedReport";
import Logo from "./assets/ethiologo.png";
import LogoEt from "./assets/et-logo.png";
import { axiosConfig } from "./config";
import EmployeeList from "./components/Employees/EmployeeList";
import AddDelegationAdmin from "./components/DelegationAdmin/AddDelegationAdmin";
import RoleManagement from "./components/Admin/RoleManagement";
import { getUserRole, hasAccess, getLoginRedirect, UserRole } from "./utils/roles";

// ─── Types ────────────────────────────────────────────────────────────────────

interface NavItemDef {
  to: string;
  icon: React.ComponentType<any>;
  label: string;
  roles: UserRole[]; // which roles can see this nav item
}

// ─── Nav Item component ───────────────────────────────────────────────────────

const NavItem = ({ to, icon: Icon, label, collapsed, active, onClick }: any) => (
  <Link
    to={to}
    onClick={onClick}
    className={`flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all duration-150 group text-sm font-medium ${active
      ? "bg-[#8dc63f]/15 text-[#76ab32]"
      : "text-slate-500 hover:bg-[#8dc63f]/8 hover:text-[#76ab32]"
      }`}
  >
    <Icon className={`w-4 h-4 shrink-0 transition-colors ${active ? "text-[#8dc63f]" : "text-slate-400 group-hover:text-[#8dc63f]"}`} strokeWidth={active ? 2.5 : 2} />
    <span className={`truncate transition-all duration-300 ${collapsed ? "lg:opacity-0 lg:w-0" : "opacity-100"}`}>{label}</span>
  </Link>
);

// ─── Unauthorized Page ────────────────────────────────────────────────────────

const Unauthorized = () => (
  <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-6 text-center px-4">
    <div className="w-20 h-20 rounded-full bg-rose-50 flex items-center justify-center">
      <ShieldOff className="w-10 h-10 text-rose-400" />
    </div>
    <div className="space-y-2">
      <h1 className="text-2xl font-black text-slate-800">Access Denied</h1>
      <p className="text-slate-500 max-w-sm font-medium">
        You do not have permission to view this page. Please contact your system administrator if you believe this is a mistake.
      </p>
    </div>
    <Link
      to="/requestlist"
      className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#8dc63f] text-white font-bold rounded-xl text-sm hover:bg-[#76ab32] transition-colors"
    >
      <ClipboardList className="w-4 h-4" />
      Go to Requests
    </Link>
  </div>
);

// ─── Protected Route ─────────────────────────────────────────────────────────

interface ProtectedRouteProps extends Omit<RouteProps, "component"> {
  component: React.ComponentType<any>;
  user: any;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  component: Component,
  user,
  path,
  ...rest
}) => (
  <Route
    path={path}
    {...rest}
    render={(props) => {
      const targetPath = typeof path === "string" ? path : props.location.pathname;
      if (hasAccess(user, targetPath)) {
        return <Component {...props} />;
      }
      return <Unauthorized />;
    }}
  />
);

// ─── Navigation Config ────────────────────────────────────────────────────────

const NAV_ITEMS: NavItemDef[] = [
  {
    to: "/dashboard",
    icon: LayoutDashboard,
    label: "Dashboard",
    roles: ["ADMIN", "MANAGER"],
  },
  {
    to: "/requestlist",
    icon: ClipboardList,
    label: "Requests",
    roles: ["ADMIN", "MANAGER", "NORMAL_USER"],
  },
  {
    to: "/sitelist",
    icon: Building2,
    label: "Sites",
    roles: ["ADMIN"],
  },
  {
    to: "/doorlist",
    icon: Store,
    label: "Doors",
    roles: ["ADMIN"],
  },
  {
    to: "/delegationlistbyadmin",
    icon: UserPlus,
    label: "Delegation",
    roles: ["ADMIN", "MANAGER"],
  },
  {
    to: "/reports",
    icon: PieChart,
    label: "Reports",
    roles: ["ADMIN", "MANAGER"],
  },
  {
    to: "/employeelist",
    icon: Users,
    label: "Employees",
    roles: ["ADMIN"],
  },
  {
    to: "/roles",
    icon: ShieldCheck,
    label: "Role Management",
    roles: ["ADMIN"],
  },
];

// ─── App Content ──────────────────────────────────────────────────────────────

const AppContent = ({ getCurrentuser, logoutUser, user, isLoggedIn }: any) => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [, setUserItem] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const location = useLocation();

  const userRole = useMemo(() => getUserRole(user), [user]);

  useEffect(() => {
    getCurrentuser()
      .then(() => setLoading(false))
      .catch(() => setLoading(false));
  }, [getCurrentuser]);

  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };
    if (showUserMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showUserMenu]);

  useEffect(() => {
    if (user?.oracleId) {
      fetch(`/api/auth/getauth/${user?.oracleId}`, axiosConfig)
        .then((response) => response.json())
        .then((response) => setUserItem(response))
        .catch((err) => console.error("User fetch error:", err));
    }
  }, [user?.oracleId]);

  // Close mobile menu and user dropdown on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setShowUserMenu(false);
  }, [location]);

  // ── Inactivity / Idle Timeout (15 minutes) ───────────────────────────────
  useEffect(() => {
    if (!isLoggedIn) return;

    // Change IDLE_TIMEOUT_MS to test with a shorter value, e.g. 10 * 1000 for 10 seconds.
    const IDLE_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes
    let timeoutId: ReturnType<typeof setTimeout>;

    const handleLogout = () => {
      // Clear auth state FIRST — this stops any further authenticated API calls
      logoutUser();
      // Then notify the user. By the time they click OK, the app has already
      // re-rendered to the login page (isLoggedIn is now false), so no
      // in-flight requests can return a 401 that reaches the UI.
      alert("You have been logged out due to 15 minutes of inactivity.");
    };

    const resetTimer = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleLogout, IDLE_TIMEOUT_MS);
    };

    const ACTIVITY_EVENTS = ["mousemove", "keydown", "click", "scroll", "touchstart"];

    // Start the initial countdown
    resetTimer();

    // Reset the countdown on any user interaction
    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, resetTimer));

    // Cleanup on unmount or when the user logs out
    return () => {
      clearTimeout(timeoutId);
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, resetTimer));
    };
  }, [isLoggedIn, logoutUser]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#8dc63f]"></div>
      </div>
    );
  }

  // ── Unauthenticated: only allow login routes ──────────────────────────────
  if (!isLoggedIn) {
    return (
      <Switch>
        <Route exact path={["/", "/login"]} component={Login} />
        <Redirect to="/login" />
      </Switch>
    );
  }


  // ── Filter sidebar items based on the authenticated user's role ───────────
  const visibleNavItems = NAV_ITEMS.filter((item) =>
    item.roles.includes(userRole)
  );

  // Default landing route based on role
  const defaultRoute = getLoginRedirect(user);

  return (
    <div className="flex min-h-screen bg-[#f7f7f7] font-sans antialiased text-[#222222] overflow-x-hidden">
      {/* Mobile Backdrop */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden backdrop-blur-sm transition-opacity duration-300"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 bg-[#f7f7f7] transition-all duration-300 ease-in-out flex flex-col
          ${mobileMenuOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
          ${collapsed ? "lg:w-16" : "lg:w-[240px]"} w-[240px]`}
      >
        {/* Logo Section */}
        <div className="h-16 flex items-center px-5 mb-2 mt-2 justify-between">
          <div className="flex items-center">
            <img src={Logo} alt="Logo" className={`h-8 transition-all duration-300 ${collapsed ? "lg:hidden" : "block"}`} />
            <img src={LogoEt} alt="Logo" className={`h-8 mx-auto transition-all duration-300 ${collapsed ? "lg:block" : "hidden"}`} />
          </div>
          <button
            className="lg:hidden p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
            onClick={() => setMobileMenuOpen(false)}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Navigation — only permitted items are rendered */}
        <nav className="flex-1 overflow-y-auto px-3 space-y-0.5 custom-scrollbar">
          {visibleNavItems.map((item) => (
            <NavItem
              key={item.to}
              to={item.to}
              icon={item.icon}
              label={item.label}
              collapsed={collapsed}
              active={location.pathname === item.to}
            />
          ))}
        </nav>

        {/* Sidebar Footer - Only visible on desktop */}
        <div className="p-4 hidden lg:block">
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="w-full flex items-center justify-center p-2 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
          >
            {collapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className={`flex-1 flex flex-col transition-all duration-300 min-w-0 ${collapsed ? "lg:ml-16" : "lg:ml-[240px]"}`}>
        {/* Top Header */}
        <header className="h-14 bg-[#f7f7f7] sticky top-0 z-30 flex items-center justify-between px-6 md:px-8 py-3">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="lg:hidden p-2 text-slate-500 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <MenuIcon className="w-6 h-6" />
            </button>
            <Link to={getLoginRedirect(user)} className="lg:hidden flex items-center shrink-0 ml-1">
              <img src={Logo} alt="Logo" className="h-8 w-auto object-contain" />
            </Link>
          </div>

          <div className="flex items-center gap-2 md:gap-4">
            {/* <button className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors relative">
              <Bell className="w-5 h-5" />
              <span className="absolute top-2 right-2.5 w-2 h-2 bg-red-500 rounded-full border-2 border-white"></span>
            </button> */}
            <div className="h-8 w-px bg-slate-200 mx-1"></div>

            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-2 md:gap-3 p-1 rounded-full hover:bg-slate-100 transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-[#8dc63f] flex items-center justify-center text-white font-bold text-xs ring-2 ring-white shadow-sm">
                  {user?.fullName?.charAt(0) || <User className="w-4 h-4" />}
                </div>
                <div className="hidden sm:block text-left mr-2">
                  <p className="text-sm font-semibold text-[#222222] leading-none">{user?.fullName || "Employee"}</p>
                  {/* <p className="text-[11px] text-slate-500 font-medium mt-1">ID: {user?.oracleId}</p> */}
                </div>
              </button>

              {showUserMenu && (
                <div className="absolute top-full right-0 mt-2 w-56 bg-white rounded-xl shadow-2xl border border-slate-100 py-2 animate-in fade-in slide-in-from-top-2 duration-200 z-50">
                  <div className="px-4 py-2 sm:hidden border-b border-slate-50">
                    <p className="text-sm font-bold text-slate-700">{user?.fullName}</p>
                    <p className="text-xs text-slate-400">ID: {user?.oracleId}</p>
                  </div>
                  <button
                    onClick={logoutUser}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    Logout Account
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-5 w-full bg-white rounded-tl-[40px] md:rounded-tl-[22px] shadow-sm border-t border-l border-slate-100 overflow-hidden relative">
          <div className="max-w-6xl mx-auto w-full">
            <Switch>
              {/* ── Routes accessible by ALL authenticated users ─────────── */}
              <Route exact path="/requestlist" component={RequestList} />
              <Route exact path="/addrequest" component={AddRequest} />
              <Route exact path="/editrequest" component={EditRequest} />

              {/* ── Protected: Admin + Manager ─────────────────────────── */}
              <ProtectedRoute exact path="/dashboard" component={Dashboard} user={user} />
              <ProtectedRoute exact path="/reports" component={UnifiedReport} user={user} />
              <ProtectedRoute exact path="/delegationlistbyadmin" component={DelegationListByAdmin} user={user} />
              <ProtectedRoute exact path="/adddelegationadmin" component={AddDelegationAdmin} user={user} />

              {/* ── Protected: Admin only ───────────────────────────────── */}
              <ProtectedRoute exact path="/sitelist" component={SiteList} user={user} />
              <ProtectedRoute exact path="/addsite" component={AddSite} user={user} />
              <ProtectedRoute exact path="/editsite" component={EditSite} user={user} />
              <ProtectedRoute exact path="/doorlist" component={DoorList} user={user} />
              <ProtectedRoute exact path="/adddoor" component={AddDoor} user={user} />
              <ProtectedRoute exact path="/editdoor" component={EditDoor} user={user} />
              <ProtectedRoute exact path="/approverlist" component={ApproverList} user={user} />
              <ProtectedRoute exact path="/addapprover" component={AddApprover} user={user} />
              <ProtectedRoute exact path="/editapprover" component={EditApprover} user={user} />
              <ProtectedRoute exact path="/employeelist" component={EmployeeList} user={user} />
              <ProtectedRoute exact path="/roles" component={RoleManagement} user={user} />

              {/* ── Default redirect based on user's role ────────────────── */}
              <Redirect to={defaultRoute} />
            </Switch>
          </div>
        </main>
      </div>
    </div>
  );
};

// ─── Redux Wiring ─────────────────────────────────────────────────────────────

const App = (props: any) => (
  <Provider store={store}>
    <Router>
      <AppContentWithRouter {...props} />
    </Router>
  </Provider>
);

const AppContentWithRouter = connect(
  (state: any) => ({
    isLoggedIn: state.auth.isLoggedIn,
    user: state.auth.user,
  }),
  { getCurrentuser, logoutUser }
)(({ ...props }: any) => (
  <ToastProvider>
    <AppContent {...props} />
  </ToastProvider>
));

export default App;
